
import { useState, useEffect } from "react";
import { 
  Mail, 
  Calendar, 
  Linkedin,
  Network,
  FileText,
  Dice1,
  BookMarked
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ConfigTabProps } from "./types";
import { AgentTool } from "@/components/candidates/types/agent";

export const ToolsConfigTab = ({ selectedAgent, updateAgentConfig }: ConfigTabProps) => {
  const [newToolName, setNewToolName] = useState("");
  const [tools, setTools] = useState<AgentTool[]>([]);
  
  useEffect(() => {
    if (selectedAgent.tools && selectedAgent.tools.length > 0) {
      setTools(selectedAgent.tools);
    } else {
      setTools([
        {
          id: 'outlook-calendar',
          name: 'Outlook Calendar',
          provider: 'outlook',
          description: 'Access and manage calendar events',
          enabled: false,
          usesMCP: true,
          icon: 'calendar'
        },
        {
          id: 'outlook-email',
          name: 'Outlook Email',
          provider: 'outlook',
          description: 'Send and read emails',
          enabled: false,
          usesMCP: true,
          icon: 'mail'
        },
        {
          id: 'linkedin',
          name: 'LinkedIn',
          provider: 'linkedin',
          description: 'Access LinkedIn profiles and connections',
          enabled: false,
          usesMCP: true,
          icon: 'linkedin'
        }
      ]);
    }
  }, [selectedAgent.id, selectedAgent.tools]);

  const handleToolToggle = (toolId: string, enabled: boolean) => {
    const updatedTools = tools.map(tool =>
      tool.id === toolId ? { ...tool, enabled } : tool
    );
    setTools(updatedTools);
    updateAgentConfig({ tools: updatedTools });
    
    const currentTool = updatedTools.find(t => t.id === toolId);
    if (enabled && needsApiCredentials(toolId)) {
      toast.info(`Please configure ${currentTool?.name} API credentials in Settings`);
    } else {
      toast.success(
        `${enabled ? 'Enabled' : 'Disabled'} ${currentTool?.name}`
      );
    }
  };

  const handleAddCustomTool = () => {
    if (!newToolName.trim()) return;
    
    const newTool: AgentTool = {
      id: `custom-${Date.now()}`,
      name: newToolName,
      provider: 'custom',
      description: 'Custom integration',
      enabled: true,
      usesMCP: false,
    };
    
    const updatedTools = [...tools, newTool];
    setTools(updatedTools);
    updateAgentConfig({ tools: updatedTools });
    setNewToolName("");
    
    toast.success(`Added new tool: ${newToolName}`);
  };

  const handleRemoveTool = (toolId: string) => {
    const updatedTools = tools.filter(tool => tool.id !== toolId);
    setTools(updatedTools);
    updateAgentConfig({ tools: updatedTools });
    
    toast.success("Tool removed");
  };

  const getToolIcon = (tool: AgentTool) => {
    switch (tool.icon) {
      case 'calendar':
        return <Calendar className="h-4 w-4" />;
      case 'mail':
        return <Mail className="h-4 w-4" />;
      case 'linkedin':
        return <Linkedin className="h-4 w-4" />;
      case 'indeed':
        return <FileText className="h-4 w-4" />;
      case 'dice-1':
        return <Dice1 className="h-4 w-4" />;
      case 'zip':
        return <BookMarked className="h-4 w-4" />;
      default:
        return <Network className="h-4 w-4" />;
    }
  };

  const needsApiCredentials = (toolId: string) => {
    return ['linkedin', 'indeed', 'dice-1', 'zip'].includes(toolId);
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Available Tools</h3>
        <p className="text-sm text-muted-foreground">
          Enable or disable tools that this agent can use to perform tasks.
          API credentials can be configured in the Settings page.
        </p>
      </div>

      <div className="space-y-4">
        {tools.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground border rounded-lg">
            No tools available for this agent.
          </div>
        ) : (
          tools.map(tool => (
            <div
              key={tool.id}
              className="flex items-center justify-between p-4 border rounded-lg"
            >
              <div className="flex items-center gap-3">
                {getToolIcon(tool)}
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium">{tool.name}</h4>
                    {tool.usesMCP && (
                      <div className="flex items-center gap-1 text-xs text-primary bg-primary/10 px-2 py-1 rounded-full">
                        <Network className="h-3 w-3" />
                        MCP
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {tool.description}
                  </p>
                </div>
              </div>
              <Switch
                checked={tool.enabled}
                onCheckedChange={(checked) => handleToolToggle(tool.id, checked)}
              />
            </div>
          ))
        )}
      </div>

      <div className="flex items-center gap-2">
        <Input
          placeholder="Add custom tool..."
          value={newToolName}
          onChange={(e) => setNewToolName(e.target.value)}
        />
        <Button onClick={handleAddCustomTool}>
          Add Tool
        </Button>
      </div>
    </div>
  );
};
